import moment from 'moment';
import React, { JSX, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSearch } from 'src/store/selectors/search';
import Actions from '../Actions';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import Label from '../Label';
import Overlay from '../Overlay';
import Table, { Hidden } from '../Table';
import CommonIssueForm from '../CommonIssueForm';
import { clearFndFormData, setFndFormData } from 'src/store/actions/fndForm';
import CommonIssuesView from './CommonIssuesView';
import { Link } from 'react-router-dom';
import Search from 'src/components/Search/Search';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import { useIssueTypes } from 'src/utils/CommonDataFetchingUtils/useIssueTypes';

interface CommonIssuesTableProps {
  commonIssues: any[];
  onSaveCommonIssue: (...args: any[]) => void;
  onDeleteCommonIssue: (id: string | number) => void;
  onSearch: (e: any) => void;
  breadcrumb: any;
}

const CommonIssuesTable = ({
  commonIssues,
  onSaveCommonIssue,
  onDeleteCommonIssue,
  onSearch,
  breadcrumb,
}: CommonIssuesTableProps): JSX.Element => {
  const dispatch = useDispatch();
  const search = useSelector(selectSearch);

  const { issueTypes } = useIssueTypes();

  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [showIssueOverlay, setShowIssueOverlay] = useState<boolean>(false);
  const [issueData, setIssueData] = useState<any>({});

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const onSetFndFormData = (data: any): void => {
    dispatch(setFndFormData(data));
  };

  const onClearFndFormData = (): void => {
    dispatch(clearFndFormData());
  };

  return (
    <>
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Common Issues..."
          onChange={onSearch}
          theme={undefined}
        />
        <Button
          color="teal"
          label="Add Common Issue"
          onClick={() => {
            onClearFndFormData();
            setShowOverlay(true);
          }}
          icon={<FontAwesomeIcon glyph="wrench" weight="light" size="small" />}
          size="small"
        />
      </Actions>

      {commonIssues && commonIssues.length > 0 ? (
        <>
          <Table
            columns={[
              {
                label: 'Description',
                sortable: true,
                cell: (data: any) => (
                  <Link
                    onClick={() => {
                      setShowIssueOverlay(true);
                      setIssueData(data);
                    }}
                  >
                    {data.description}
                  </Link>
                ),
              },
              {
                xsHide: true,
                label: 'Type',
                cell: (data: any) => data.issueTypeName,
              },
              {
                label: 'Asset',
                cell: (data: any) => data.itemName,
              },
              {
                label: 'Submitted By',
                cell: (data: any) => data.userName,
              },
              {
                xsHide: true,
                label: 'Submitted',
                cell: (data: any) => {
                  return (
                    <Label
                      color={'teal'}
                      label={moment(data.createdOn).format('MM/DD/YYYY')}
                    />
                  );
                },
              },
              {
                cell: (data: any) => (
                  <Hidden>
                    <Button
                      color="semantic"
                      label={<FontAwesomeIcon glyph="pencil" weight="light" />}
                      onClick={() => {
                        setShowOverlay(true);
                        onClearFndFormData();
                        onSetFndFormData({
                          ...data,
                          attachment: data.attachment
                            ? JSON.parse(data.attachment)
                            : [],
                        });
                      }}
                    />
                    <Button
                      color="semantic"
                      onClick={(e: any) =>
                        window.confirm(
                          `Are you sure you want to delete common issue?`
                        ) && onDeleteCommonIssue(data.id)
                      }
                      label={<FontAwesomeIcon glyph="trash" weight="light" />}
                    />
                  </Hidden>
                ),
              },
            ]}
            data={commonIssues}
            paginate={{ itemsPerPage, position: 'both' }}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </>
      ) : (
        <Empty
          direction="ttb"
          headline={'Your common issues inbox is empty'}
          strapline={''}
          searchIsActive={
            search && search.commonIssues ? search.commonIssues : ''
          }
          cta={
            <Button
              color="teal"
              label="Add Common Issue"
              onClick={() => {
                onClearFndFormData();
                setShowOverlay(true);
              }}
              icon={
                <FontAwesomeIcon glyph="wrench" weight="light" size="small" />
              }
              size="small"
            />
          }
          image={
            <img
              alt="Your common issues inbox is empty"
              src="/assets/empty/service-requests.svg"
            />
          }
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <CommonIssueForm
            onClose={() => setShowOverlay(false)}
            onSaveCommonIssue={onSaveCommonIssue}
            breadcrumb={breadcrumb}
            types={issueTypes}
          />
        }
      />
      <Overlay
        showOverlay={showIssueOverlay}
        content={
          <CommonIssuesView
            data={issueData}
            onClose={() => setShowIssueOverlay(false)}
          />
        }
      />
    </>
  );
};

export default CommonIssuesTable;
